import { create } from "zustand";
import { createClient, fromSupabaseUser, fromSupabaseSession } from "@bastion/auth";
import type { User, AuthSession } from "@bastion/auth";

let authStateListenerRegistered = false;

const OAUTH_REDIRECT_PATH_KEY = "bastion.oauth.redirectPath";

interface AuthState {
  user: User | null;
  session: AuthSession | null;
  isLoading: boolean;
  error: string | null;

  initialize: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

/**
 * Get (or create) the Supabase client configured for web OAuth.
 *
 * `detectSessionInUrl: true` lets Supabase automatically pick up the
 * OAuth code/tokens from the redirect URL, so we don't need a manual
 * callback handler. This is the standard web flow (as opposed to Tauri's
 * loopback server approach).
 */
function getSupabase() {
  return createClient(undefined, { detectSessionInUrl: true });
}

function currentRelativeUrl(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function rememberOAuthRedirectPath(): void {
  const next = currentRelativeUrl();
  if (next === "/") return;
  window.localStorage.setItem(OAUTH_REDIRECT_PATH_KEY, next);
}

function restoreOAuthRedirectPath(): void {
  const next = window.localStorage.getItem(OAUTH_REDIRECT_PATH_KEY);
  window.localStorage.removeItem(OAUTH_REDIRECT_PATH_KEY);
  if (!next || !next.startsWith("/") || next.startsWith("//")) return;
  if (next === currentRelativeUrl()) return;
  window.history.replaceState(null, "", next);
  // React Router listens to browser navigation events, not raw history state
  // mutation. Dispatch popstate so the address bar and rendered route stay in
  // sync after OAuth returns to the origin callback URL.
  const event =
    typeof PopStateEvent === "function"
      ? new PopStateEvent("popstate", { state: null })
      : new Event("popstate");
  window.dispatchEvent(event);
}

function clearOAuthRedirectPath(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(OAUTH_REDIRECT_PATH_KEY);
  } catch {
    // localStorage can be unavailable in tests, private mode, or blocked contexts.
  }
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  session: null,
  isLoading: true,
  error: null,

  initialize: async () => {
    set({ isLoading: true, error: null });
    try {
      const supabase = getSupabase();

      // On page load, Supabase automatically detects OAuth params in the URL
      // (because detectSessionInUrl: true) and exchanges them for a session.
      // getSession() returns the result of that exchange.
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error) {
        set({ isLoading: false, error: error.message });
        return;
      }

      if (session) {
        const user = fromSupabaseUser(session.user);
        const authSession = fromSupabaseSession(session, user);
        set({ user, session: authSession, isLoading: false });
        restoreOAuthRedirectPath();
      } else {
        set({ isLoading: false });
      }

      if (!authStateListenerRegistered) {
        authStateListenerRegistered = true;
        // Listen for auth state changes (session refresh, sign-out, etc.)
        supabase.auth.onAuthStateChange((event, session) => {
          if (event === "SIGNED_IN" && session) {
            const user = fromSupabaseUser(session.user);
            const authSession = fromSupabaseSession(session, user);
            set({ user, session: authSession, isLoading: false });
          } else if (event === "SIGNED_OUT") {
            set({ user: null, session: null, isLoading: false });
          }
        });
      }
    } catch {
      set({ isLoading: false, error: "Failed to initialize auth" });
    }
  },

  signInWithGoogle: async () => {
    set({ isLoading: true, error: null });
    try {
      const supabase = getSupabase();
      rememberOAuthRedirectPath();
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          // Keep this origin-only so Supabase's allowlist stays simple. The
          // intended path is restored from localStorage after OAuth completes.
          redirectTo: window.location.origin,
        },
      });
      if (error) {
        set({ isLoading: false, error: error.message });
      }
      // Browser navigates away to Google at this point.
      // When it comes back, initialize() runs again and picks up the session.
    } catch {
      set({ isLoading: false, error: "Failed to sign in" });
    }
  },

  signInWithPassword: async (email: string, password: string) => {
    set({ isLoading: true, error: null });
    try {
      clearOAuthRedirectPath();
      const supabase = getSupabase();
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) {
        set({ isLoading: false, error: error.message });
        return;
      }
      if (data.session) {
        const user = fromSupabaseUser(data.session.user);
        const authSession = fromSupabaseSession(data.session, user);
        set({ user, session: authSession, isLoading: false });
      } else {
        set({ isLoading: false });
      }
    } catch {
      set({ isLoading: false, error: "Failed to sign in" });
    }
  },

  signOut: async () => {
    try {
      const supabase = getSupabase();
      await supabase.auth.signOut();
      set({ user: null, session: null });
    } catch {
      set({ error: "Failed to sign out" });
    }
  },
}));
